import React from "react";

/**
 * The `useInterval()` hook repeatedly calls a function with a fixed time
 * delay between each call.
 *
 * @param callback - A function to be executed every `delay` milliseconds.
 * The function is not passed any arguments, and no return value is expected.
 * @param delay - The time, in milliseconds (thousandths of a second),
 * the timer should delay in between executions of the specified function.
 * If `null` or `undefined`, the interval is on pause.
 * @param args - Additional arguments which are passed through to the function
 * specified by `callback` once the timer expires.
 */
function useInterval(
  callback: (...args: any[]) => void,
  delay?: number | null,
  ...args: any[]
): void {
  const savedCallback = React.useRef<(...args: any[]) => void>();

  // Remember the latest callback.
  React.useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  // Set up the interval.
  React.useEffect(() => {
    function tick() {
      savedCallback && savedCallback.current && savedCallback.current(...args);
    }
    if (delay !== null && delay !== undefined) {
      let id = setInterval(tick, delay);
      return () => clearInterval(id);
    }
  }, [args, delay]);
}

export default useInterval;
